'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { Paperclip } from 'lucide-react'
import { useState } from 'react'
import { z } from 'zod'

import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Form,
  FormControl,
  FormField,
  FormItem,
} from '@/components/ui'
import { useForm } from '@/hooks/useForm'
import { fileTypeSchema } from '@/validations/schemas/fileTypeSchema'

import { Dropzone } from './dropzone'
import { FileIcon } from './FileIcon'

import type { LocalAttachment } from './workflowAttachment'

type WorkflowDialogAddAttachmentProps = {
  stepName: string
  onAddAttachments: (files: LocalAttachment[]) => void
}

const formSchema = z.object({
  files: fileTypeSchema.array().min(1, { message: 'Arquivo é obrigatório' }),
})

export type FormData = z.infer<typeof formSchema>

export function WorkflowDialogAddAttachment({
  stepName,
  onAddAttachments,
}: WorkflowDialogAddAttachmentProps) {
  const [open, setOpen] = useState(false)
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      files: [],
    },
  })

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const data = form.getValues()
    if (data.files.length > 0) {
      onAddAttachments(data.files)
      handleExit()
    }
  }

  const isDisabled = form.watch('files').length === 0 || form.buttonDisabled

  const handleExit = () => {
    setOpen(false)
    form.reset()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => (isOpen ? setOpen(true) : handleExit())}
    >
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          className="flex items-center gap-2 text-system-text-secondary"
        >
          <Paperclip className="size-4" />
          Adicionar anexo
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px]">
        <form onSubmit={onSubmit}>
          <Form {...form}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Paperclip className="size-6" />
                Adicionar anexo
              </DialogTitle>
              <DialogDescription className="mt-4 text-base text-system-text-primary">
                <b>Estágio:</b> {stepName}
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-4 py-4">
              <FormField
                control={form.control}
                name="files"
                render={({ field, fieldState }) => {
                  const { error } = fieldState

                  return (
                    <FormItem className="w-full">
                      <FormControl>
                        <Dropzone
                          files={field.value ?? []}
                          onChange={field.onChange}
                          mimeType={[
                            'image/*',
                            'application/pdf',
                            'application/msword',
                            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                            'application/vnd.ms-excel',
                            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                            'application/vnd.ms-powerpoint',
                            'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                          ]}
                          error={error?.message}
                          maxFile={3}
                        />
                      </FormControl>
                    </FormItem>
                  )
                }}
              />

              <div className="flex flex-col gap-2">
                {form.watch('files').map((file, index) => {
                  return (
                    <div
                      key={index}
                      className="flex items-center justify-between gap-2 rounded-md border p-2 px-4"
                    >
                      <div className="flex w-full items-center gap-4">
                        <FileIcon mimeType={file.file?.type || ''} />
                        <div className="flex w-full flex-col gap-2">
                          <span className="text-sm font-medium text-system-text-primary">
                            {file.file?.name}
                          </span>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="social">
                  Cancelar
                </Button>
              </DialogClose>
              <Button type="submit" variant="slate" disabled={isDisabled}>
                Adicionar anexo
              </Button>
            </DialogFooter>
          </Form>
        </form>
      </DialogContent>
    </Dialog>
  )
}
